import { useCallback, useMemo, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { useActiveRealmAPIContext } from '@interxfi/api-client';
import {
  buildExcMarketdataV1MarketDataServiceGetEnhancedChartDataQueryOptions,
  useExcMarketdataV1MarketDataServiceGetEnhancedChartData,
} from '@interxfi/api-client/generated/hooks/exc-marketdata-v1-market-data-service-get-enhanced-chart-data';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { LineChartOptions } from '@interxfi/design-system/components/chart/line/line-chart';
import { StockPriceLineChart, type StockPriceLineChartData } from '@interxfi/design-system/components/chart/line/stock-price-line-chart';
import { DateFormat, type DateFormatProps } from '@interxfi/design-system/components/format/date';
import { FancyRadioGroupInput, type FancyRadioGroupOption } from '@interxfi/design-system/components/input/fancy/radio/fancy-radio-group-input';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import type { TypographyProps } from '@interxfi/design-system/components/typography';
import { ExcMarketdataV1ChartPeriod, type ExcMarketdataV1MarketDataServiceGetEnhancedChartDataGetEnhancedChartDataRequest } from '@interxfi/types';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../util/error-handler';
import { useSetMarketDataInspection } from './market-data-inspection';
import type { MarketDataPriceBaseline } from './market-data-quote-price-display';
import styles from './market-data-chart.module.scss';

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load chart data', 'view market data for this asset');

const CHART_PERIOD_OPTIONS = [
  { label: '1D', value: ExcMarketdataV1ChartPeriod.OneDay },
  { label: '1W', value: ExcMarketdataV1ChartPeriod.OneWeek },
  { label: '1M', value: ExcMarketdataV1ChartPeriod.OneMonth },
  { label: '3M', value: ExcMarketdataV1ChartPeriod.ThreeMonths },
  { label: 'YTD', value: ExcMarketdataV1ChartPeriod.YearToDate },
  { label: '1Y', value: ExcMarketdataV1ChartPeriod.OneYear },
  { label: '5Y', value: ExcMarketdataV1ChartPeriod.FiveYears },
  { label: '10Y', value: ExcMarketdataV1ChartPeriod.TenYears },
] satisfies FancyRadioGroupOption<ExcMarketdataV1ChartPeriod>[];

function buildChartDataRequest(
  symbol: string,
  period: ExcMarketdataV1ChartPeriod,
): ExcMarketdataV1MarketDataServiceGetEnhancedChartDataGetEnhancedChartDataRequest {
  return { symbol, period, downsample: true };
}

export function prefetchMarketDataChartPeriods(
  queryClient: QueryClient,
  apiContext: ReturnType<typeof useActiveRealmAPIContext>,
  isAuthenticated: boolean,
  symbol: string,
) {
  const { activeRealm, activeTenantContext, baseUrl, extendKey, token } = apiContext;

  return Promise.all(
    CHART_PERIOD_OPTIONS.map(({ value: period }) => {
      const options = buildExcMarketdataV1MarketDataServiceGetEnhancedChartDataQueryOptions(
        baseUrl,
        isAuthenticated,
        token,
        activeRealm,
        activeTenantContext,
        extendKey,
        buildChartDataRequest(symbol, period),
      );

      return options.enabled === true ? queryClient.query(options).catch(() => undefined) : Promise.resolve();
    }),
  );
}

const chartOptions = {
  maintainAspectRatio: false,
  scales: {
    x: { display: false },
    y: { display: false },
  },
} as const satisfies LineChartOptions;

const periodLabelTypography = {
  variant: 'mono',
  weight: 'medium',
  size: '0.875rem',
  lineHeight: '1.125rem',
  tone: 'neutral-dk',
} as const satisfies TypographyProps;

const periodEndLabelTypography = {
  ...periodLabelTypography,
  align: 'right',
} as const satisfies TypographyProps;

function getPeriodLabelFormat(period: ExcMarketdataV1ChartPeriod): Pick<DateFormatProps, 'hourCycle' | 'wellKnownType'> {
  if (period === ExcMarketdataV1ChartPeriod.OneDay) {
    return { hourCycle: 'h12', wellKnownType: 'time_hh_mm' };
  }

  return { wellKnownType: 'date_iso' };
}

interface MarketDataChartProps {
  isPending?: boolean;
  symbol: string | undefined;
}

export function MarketDataChart({ isPending, symbol }: MarketDataChartProps) {
  const [period, setPeriod] = useState<ExcMarketdataV1ChartPeriod>(ExcMarketdataV1ChartPeriod.OneDay);
  const {
    data,
    error,
    isPending: chartIsPending,
  } = useExcMarketdataV1MarketDataServiceGetEnhancedChartData(symbol ? buildChartDataRequest(symbol, period) : undefined);
  useErrorHandler(error, errorMessageGetter);
  const points = data?.points;
  const setInspection = useSetMarketDataInspection();
  const chartData = useMemo<StockPriceLineChartData | undefined>(
    () =>
      points
        ? {
            labels: points.map((point) => point.time),
            dataset: { data: points.map((point) => Number(point.price)) },
          }
        : undefined,
    [points],
  );

  const periodStartPrice = points?.[0]?.price;
  const baseline = useMemo<MarketDataPriceBaseline>(
    () =>
      period === ExcMarketdataV1ChartPeriod.OneDay || periodStartPrice === undefined
        ? { type: 'previousClose' }
        : { type: 'price', value: periodStartPrice },
    [period, periodStartPrice],
  );

  const handleInspect = useCallback(
    (dataIndex: number | null) => {
      const point = dataIndex === null ? undefined : points?.[dataIndex];

      setInspection(point ? { point, baseline } : null);
    },
    [points, setInspection, baseline],
  );

  const periodLabelFormat = getPeriodLabelFormat(period);
  const showPending = Boolean(isPending || (symbol && (chartIsPending || error)));

  return (
    <VerticalStack className={styles.root} gap={4}>
      <div className={styles.chart}>
        {showPending || !chartData ? (
          <Skeleton fillParent type="rect" />
        ) : (
          <StockPriceLineChart data={chartData} onInspect={handleInspect} options={chartOptions} />
        )}
      </div>

      <HorizontalStack align="space-between" blockAlign="center" gap={4} wrap="nowrap">
        {showPending ? (
          <>
            <Skeleton charCount={8} />
            <Skeleton charCount={8} />
          </>
        ) : (
          <>
            <DateFormat {...periodLabelFormat} typography={periodLabelTypography} value={points?.[0]?.time} />
            <DateFormat {...periodLabelFormat} typography={periodEndLabelTypography} value={points?.[points.length - 1]?.time} />
          </>
        )}
      </HorizontalStack>

      <FancyRadioGroupInput
        className={styles.periodPicker}
        hideLabel
        label="Chart Period"
        name="chartPeriod"
        onChange={(e) => {
          setPeriod(e.target.value as ExcMarketdataV1ChartPeriod);
        }}
        options={CHART_PERIOD_OPTIONS}
        size="small"
        value={period}
      />
    </VerticalStack>
  );
}
